import React, { useState } from 'react';
import { useAdminSearches, useAdminSearchDetail, useExportSearchCSV } from '@/hooks/api/useAdmin';
import { LoadingSpinner, TableSkeleton } from '@/components/ui';
import type { AdminSearchSummaryResponse } from '@/services/admin.service';

interface SearchDataManagementProps {
  className?: string;
  pageSize?: number;
}

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'extracted', label: 'Extracted' },
  { value: 'failed', label: 'Failed' },
  { value: 'partial', label: 'Partial' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  extracted: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  partial: 'bg-orange-100 text-orange-800',
};

export const SearchDataManagement: React.FC<SearchDataManagementProps> = ({
  className = '',
  pageSize = 50,
}) => {
  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [extractionStatus, setExtractionStatus] = useState('');

  // Applied filters (only update on "Apply")
  const [appliedFilters, setAppliedFilters] = useState<{
    start_date?: string;
    end_date?: string;
    state?: string;
    city?: string;
    extraction_status?: string;
  }>({});

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const offset = (currentPage - 1) * pageSize;

  // Expanded detail row
  const [expandedSearchId, setExpandedSearchId] = useState<string | null>(null);

  // Query data
  const {
    data: searchData,
    isLoading,
    error,
  } = useAdminSearches({
    limit: pageSize,
    offset,
    ...appliedFilters,
  });

  const { data: detailData, isLoading: isLoadingDetail } = useAdminSearchDetail(
    expandedSearchId || '',
    !!expandedSearchId
  );

  const exportCSV = useExportSearchCSV();

  const searches = searchData?.searches ?? [];
  const total = searchData?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    setExpandedSearchId(null);
    setAppliedFilters({
      start_date: startDate || undefined,
      end_date: endDate || undefined,
      state: state || undefined,
      city: city || undefined,
      extraction_status: extractionStatus || undefined,
    });
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setState('');
    setCity('');
    setExtractionStatus('');
    setCurrentPage(1);
    setExpandedSearchId(null);
    setAppliedFilters({});
  };

  const handleExportCSV = () => {
    exportCSV.mutate(appliedFilters);
  };

  const handleRowClick = (searchId: string) => {
    setExpandedSearchId(expandedSearchId === searchId ? null : searchId);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (value: number | null) => {
    if (value === null || value === undefined) return '-';
    return `$${value.toFixed(2)}`;
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
            <input
              type="text"
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              placeholder="e.g. CA"
              maxLength={2}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g. Los Angeles"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={extractionStatus}
              onChange={(e) => setExtractionStatus(e.target.value)}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleApplyFilters}
              className="px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-md hover:bg-purple-700 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-300 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Summary Row */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-600">
          {total > 0
            ? `Showing ${offset + 1}-${Math.min(offset + pageSize, total)} of ${total} results`
            : 'No results'}
        </p>
        <button
          onClick={handleExportCSV}
          disabled={exportCSV.isPending || total === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {exportCSV.isPending ? (
            <>
              <LoadingSpinner size="sm" />
              Exporting...
            </>
          ) : (
            'Export CSV'
          )}
        </button>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          Failed to load search data. Please try again.
        </div>
      )}

      {/* Loading State */}
      {isLoading && <TableSkeleton rows={5} />}

      {/* Data Table */}
      {!isLoading && !error && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    State
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Job Description
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Municipality
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fee
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {searches.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No searches match the selected filters.
                    </td>
                  </tr>
                ) : (
                  searches.map((search: AdminSearchSummaryResponse) => (
                    <React.Fragment key={search.id}>
                      <tr
                        onClick={() => handleRowClick(search.id)}
                        className={`cursor-pointer hover:bg-gray-50 transition-colors ${
                          expandedSearchId === search.id ? 'bg-purple-50' : ''
                        }`}
                      >
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatDate(search.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">{search.city}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{search.state}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 max-w-xs">
                          {truncateText(search.job_description, 100)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {search.municipality_name || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">
                          {formatCurrency(search.total_estimated_fee)}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                          {search.fee_confidence || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              STATUS_COLORS[search.extraction_status] || 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {search.extraction_status.replace('_', ' ')}
                          </span>
                        </td>
                      </tr>

                      {/* Expandable Detail Row */}
                      {expandedSearchId === search.id && (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 bg-gray-50">
                            <SearchDetailPanel
                              searchId={search.id}
                              detailData={detailData}
                              isLoading={isLoadingDetail}
                              onClose={() => setExpandedSearchId(null)}
                              formatCurrency={formatCurrency}
                              formatDate={formatDate}
                            />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setCurrentPage((p) => Math.max(1, p - 1));
              setExpandedSearchId(null);
            }}
            disabled={currentPage === 1}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => {
              setCurrentPage((p) => Math.min(totalPages, p + 1));
              setExpandedSearchId(null);
            }}
            disabled={currentPage === totalPages}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Detail Panel Sub-component

interface SearchDetailPanelProps {
  searchId: string;
  detailData: ReturnType<typeof useAdminSearchDetail>['data'];
  isLoading: boolean;
  onClose: () => void;
  formatCurrency: (value: number | null) => string;
  formatDate: (dateString: string) => string;
}

function SearchDetailPanel({
  detailData,
  isLoading,
  onClose,
  formatCurrency,
  formatDate,
}: SearchDetailPanelProps) {
  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (!detailData) {
    return <p className="text-gray-500 py-4">Failed to load detail data.</p>;
  }

  const d = detailData;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <h3 className="text-lg font-semibold text-gray-900">Search Detail</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
        >
          &times;
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Search Info */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-1">Search Info</h4>
          <DetailRow label="City" value={d.city} />
          <DetailRow label="State" value={d.state} />
          <DetailRow label="Job Description" value={d.job_description} />
          <DetailRow label="Municipality" value={d.municipality_name || 'N/A'} />
          <DetailRow label="Permit Type" value={d.permit_type_name || 'N/A'} />
          {d.permit_link && (
            <DetailRow
              label="Permit Link"
              value={
                <a
                  href={d.permit_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:text-purple-800 underline break-all"
                >
                  {d.permit_link}
                </a>
              }
            />
          )}
        </div>

        {/* Fee Info */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-1">Fee Info</h4>
          <DetailRow label="Total Fee" value={formatCurrency(d.total_estimated_fee)} />
          <DetailRow label="Fee Confidence" value={d.fee_confidence || 'N/A'} />
          <DetailRow
            label="Extraction Confidence"
            value={
              d.extraction_confidence !== null
                ? `${(d.extraction_confidence * 100).toFixed(0)}%`
                : 'N/A'
            }
          />
          {d.fee_breakdown && (
            <div>
              <span className="text-sm font-medium text-gray-600">Fee Breakdown:</span>
              <ul className="mt-1 space-y-1">
                {Object.entries(d.fee_breakdown).map(([key, value]) => {
                  if (key === 'calculation_steps' && Array.isArray(value)) {
                    return (
                      <li key={key} className="text-sm text-gray-700 pl-2">
                        <span className="font-medium">{key}:</span>
                        <ul className="mt-1 space-y-2 pl-2">
                          {(value as Array<Record<string, unknown>>).map((step, i) => (
                            <li key={i} className="text-xs text-gray-600 border-l-2 border-gray-200 pl-2">
                              <div><span className="font-medium">Step {String(step.step)}:</span> {String(step.description)}</div>
                              <div>Method: {String(step.method)} | Amount: {formatCurrency(step.amount as number)}</div>
                              {Boolean(step.calculation) && <div>Calc: {String(step.calculation)}</div>}
                              {Boolean(step.reasoning) && <div className="italic text-gray-500">{String(step.reasoning)}</div>}
                            </li>
                          ))}
                        </ul>
                      </li>
                    );
                  }
                  if (Array.isArray(value)) {
                    // Skip other array fields not handled above
                    return null;
                  }
                  let display: string;
                  if (typeof value === 'number') {
                    display = formatCurrency(value);
                  } else if (typeof value === 'boolean') {
                    display = value ? 'Yes' : 'No';
                  } else if (value === null || value === undefined) {
                    display = 'N/A';
                  } else if (typeof value === 'object') {
                    display = JSON.stringify(value);
                  } else {
                    display = String(value);
                  }
                  return (
                    <li key={key} className="text-sm text-gray-700 pl-2">
                      {key}: {display}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        {/* Processing Info */}
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-1">
            Processing Info
          </h4>
          <DetailRow
            label="Status"
            value={
              <span
                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  STATUS_COLORS[d.extraction_status] || 'bg-gray-100 text-gray-800'
                }`}
              >
                {d.extraction_status.replace('_', ' ')}
              </span>
            }
          />
          <DetailRow
            label="Duration"
            value={
              d.processing_duration_ms !== null
                ? `${(d.processing_duration_ms / 1000).toFixed(1)}s`
                : 'N/A'
            }
          />
          <DetailRow label="Search Cost" value={formatCurrency(d.search_cost)} />
          <DetailRow label="LLM Cost" value={formatCurrency(d.llm_cost)} />
          <DetailRow label="Web Search Cost" value={formatCurrency(d.web_search_cost)} />
          <DetailRow label="LLM Enhanced" value={d.llm_enhanced ? 'Yes' : 'No'} />
          <DetailRow label="Created" value={formatDate(d.created_at)} />
        </div>
      </div>

      {/* User Feedback */}
      {(d.user_rating !== null || d.user_feedback) && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900 border-b border-gray-200 pb-1">
            User Feedback
          </h4>
          {d.user_rating !== null && (
            <DetailRow
              label="Rating"
              value={'★'.repeat(d.user_rating) + '☆'.repeat(5 - d.user_rating)}
            />
          )}
          {d.user_feedback && <DetailRow label="Feedback" value={d.user_feedback} />}
        </div>
      )}

      {/* Error Section */}
      {d.error_message && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <h4 className="font-medium text-red-800 mb-1">Error</h4>
          <p className="text-sm text-red-700">{d.error_message}</p>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col">
      <span className="text-sm font-medium text-gray-600">{label}</span>
      <span className="text-sm text-gray-900 break-words">{value}</span>
    </div>
  );
}

export default SearchDataManagement;
